import * as eventTypes from './eventTypes';
import * as actionTypes from './actionTypes';
import api from './api';
import { sendHttpCommandAndWaitForEvent, sendHttpRequest } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';

export function createPartnerFile(partner) {
  return async (dispatch, getState) => {
    try {
      const config = getState().offerConfig;
      dispatch({ type: actionTypes.CREATE_PARTNER_FILE_STARTED });

      const ajaxProm = api.createPartnerFile(createClientRequestBody(partner, config));

      const event = await dispatch(sendHttpCommandAndWaitForEvent(ajaxProm, 'RunningOfferCommand', [eventTypes.PARTNER_FILE_CREATED]));

      if (event.ok) {
        const { documentId, siteId, partnerId } = event.eventBody;
        await dispatch({
          type: actionTypes.CREATE_PARTNER_FILE_SUCCEEDED,
          payload: { documentId, siteId, partnerId }
        });
      } else {
        dispatch({ type: actionTypes.CREATE_PARTNER_FILE_FAILED });
      }
    } catch (error) {
      dispatch({ type: actionTypes.CREATE_PARTNER_FILE_FAILED });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

function createClientRequestBody(client, config) {
  const { generalData, addresses, partnerType } = client;

  const clientAddresses = JSON.parse(JSON.stringify(addresses));
  clientAddresses.forEach(address => {
    address.countryId = address.country ? address.country.id : null;
    address.countyId = address.county ? address.county.id : null;
    address.cityId = address.city ? address.city.id : null;
  });

  return {
    ...client,
    documentId: -1,
    siteId: -1,
    generalData: {
      ...generalData,
      relationId: generalData.relationId || config.defaultRelationId,
      isAssociatedDocumentPartner: false
    },
    partnerType: {
      partnerTypeId: partnerType.partnerTypeId || config.defaultPartnerType.partnerTypeId,
      partnerTypeCode: partnerType.partnerTypeCode || config.defaultPartnerType.partnerTypeCode,
      partnerTypeName: partnerType.partnerTypeName || config.defaultPartnerType.partnerTypeName
    },
    addresses: clientAddresses
  };
}

export function loadPartnerFileHeader(documentId, siteId) {
  return async (dispatch, getState) => {
    let key = `${documentId}-${siteId}`;
    let partnerFileHeader = getState().partnerFiles.item[key];

    let shouldCallApi = !partnerFileHeader || (!partnerFileHeader.loaded && !partnerFileHeader.loading);
    if (!shouldCallApi) return;

    try {
      dispatch({ type: actionTypes.LOAD_PARTNER_FILE_HEADER_STARTED, key });

      const response = await dispatch(sendHttpRequest(api.getPartnerFileHeader(documentId, siteId)));

      dispatch({
        type: actionTypes.LOAD_PARTNER_FILE_HEADER_SUCCESS,
        payload: response,
        key
      });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_PARTNER_FILE_HEADER_FAILED, key, error });
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function computePartnerFileNextStates(documentId, siteId) {
  return async (dispatch, getState) => {
    const nextStates = getState().currentFolder.nextStates;
    if (nextStates.isLoaded || nextStates.loading) {
      return;
    }

    try {
      dispatch({ type: actionTypes.COMPUTE_PARTNER_FILE_NEXT_STATES_STARTED, key: `${documentId}-${siteId}` });

      await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.computePartnerFileNextStates(documentId, siteId),
          'ComputePartnerFileNextStatesInProgress',
          [eventTypes.PARTNER_FILE_NEXT_STATES_LIST_COMPUTED],
          false
        )
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updatePartnerFileState(documentId, siteId, stateId, comment, reasonsIds) {
  return async dispatch => {
    try {
      await dispatch(
        sendHttpCommandAndWaitForEvent(
          api.updatePartnerFileState(documentId, siteId, stateId, comment, reasonsIds),
          'UpdatePartnerFileStateInProgress',
          [eventTypes.PARTNER_FILE_STATE_UPDATED]
        )
      );
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadPartnerFiles(filters) {
  return async dispatch => {
    try {
      const partnerFiles = await dispatch(sendHttpRequest(api.getPartnerFiles(filters)));
      dispatch({ type: actionTypes.LOAD_PARTNER_FILES_SUCCESS });

      return partnerFiles;
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function exportPartnerFiles(filters, fieldsToShow) {
  return dispatch => {
    return api.exportPartnerFiles(filters, fieldsToShow).catch(err => {
      dispatch(showError(err.message));
      throw err;
    });
  };
}

export function buildPartnerFileExportDownloadUrl(filePath, fileName) {
  return () => api.buildPartnerFileExportDownloadUrl(filePath, fileName);
}

export function loadUserPreferences() {
  return async (dispatch, getState) => {
    try {
      let allUserPreferences = getState().partnerFiles.userPreferences;
      if (allUserPreferences.isLoaded) return;

      allUserPreferences = await dispatch(sendHttpRequest(api.loadUserPreferences()));
      dispatch({ type: actionTypes.LOAD_PARTNER_FILE_USER_PREFERENCES_SUCCESS, payload: allUserPreferences });

      const implicitPreference = allUserPreferences.find(x => x.implicit === true);
      dispatch({ type: actionTypes.LOAD_PARTNER_FILE_SELECTED_USER_PREFERENCE_SUCCESS, payload: implicitPreference });

      const defaultPreference = allUserPreferences.find(x => x.isDefault === true);
      dispatch({ type: actionTypes.LOAD_PARTNER_FILE_DEFAULT_USER_PREFERENCE_SUCCESS, payload: defaultPreference });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updateUserPreferences(userPreferences) {
  return async dispatch => {
    try {
      await dispatch(sendHttpRequest(api.updateUserPreferences(userPreferences)));
      dispatch({ type: actionTypes.UPDATE_PARTNER_FILE_USER_PREFERENCES_SUCCESS, payload: userPreferences });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function updateSelectedUserPreference(selectedUserPreference) {
  return async dispatch => {
    try {
      dispatch({
        type: actionTypes.UPDATE_PARTNER_FILE_SELECTED_USER_PREFERENCE_SUCCESS,
        payload: selectedUserPreference
      });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}
